import raw from "@/data/preview-capture.json";

/* ────────────────────────────────────────────────────────────────────────────
 *  previewCapture — the one reading behind every number on /preview.
 *
 *  `src/data/preview-capture.json` is written by scripts/capture-product-
 *  sidebar.ts at the end of the SAME session that cuts every product image
 *  the marketing site shows (the map, the six panel views, the dossier pages,
 *  the film). So the composite the page prints, the layer dots on the tabs,
 *  the chapter times under the film and every bar on the "measured for
 *  years" timeline are read off that run — nothing here is typed into a
 *  component (§Marketing claims). Re-capture ⇒ the page updates itself.
 *
 *  The type below is the contract the page builds against; the script is
 *  the only writer (scripts/lib/preview-facts.ts).
 * ──────────────────────────────────────────────────────────────────────────── */

export type LayerKey = "valuation" | "building" | "location" | "neighborhood" | "investment";

export interface PreviewFilm {
  /** How much faster than real time the scan segment plays. */
  speed: number;
  durationS: number;
  /** Seconds into the cut film at which each chapter begins. */
  chapters: { search: number; scan: number; verdict: number; layer: number };
  raw: Record<string, number>;
  ring?: { t: number; cx: number; cy: number; r: number }[];
}

/** A measurement with an observation window, in whole years. */
interface Span { from: number; to: number; points: number; score: number | null }

export interface PreviewCapture {
  capturedAt: string;
  address: string;
  postcode: string | null;
  city: string;
  buurt: string | null;
  composite: number;
  tier: string;
  confidence: number;
  sources: { loaded: number; total: number };
  layers: Record<LayerKey, number>;
  built: number | null;
  film: PreviewFilm | null;
  evidence: {
    market: Span | null;
    woz: (Span & { cagrPct: number; value: number | null; perSqm: number | null }) | null;
    solar: { yieldKwhPerKwp: number; years: number; from: number; to: number; score: number | null } | null;
    groundMotion: { mmPerYear: number; period: string | null; distanceM: number | null; score: number | null } | null;
    crime: (Span & { months: number | null; trend: string | null }) | null;
    energy: { label: string | null; registered: number | null; validUntil: number | null; score: number | null } | null;
    livability: { score5: number; class9: number | null; scale: "grid" | "buurt" | null; vintage: number; score: number | null } | null;
    populationForecast: { year: number; growthPct: number; score: number | null } | null;
    viewshed: {
      buildings: number | null; skyView: number | null; blockedSectors: number | null; eyeHeightM: number | null;
      skyScore: number | null; obstructionScore: number | null;
    } | null;
    streetTrees: { count: number; radiusM: number; covered: boolean | null; score: number | null } | null;
    transitStops: number | null;
    wkpb: number | null;
    foundation: { riskLevel: string | null; percPre1970: number | null } | null;
    height3d: number | null;
  };
}

export const CAPTURE = raw as unknown as PreviewCapture;

export const LAYER_KEYS: readonly LayerKey[] = ["valuation", "building", "location", "neighborhood", "investment"] as const;
